import React, { useEffect, useMemo, useState } from 'react';
import AppWrapper from 'components/AppWrapper';
import { useChargeCardBillingPlans } from 'components/modals/RequestCard/TypeAndPlan/useBillingPlans';
import { useTranslation } from 'react-i18next';
import { tryStoringPaymentInstrument } from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import { useProductInstrumentation } from 'contexts/productInstrumentation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSubmitCardRequest } from 'components/modals/RequestCard/api';
import { useEnabledPaymentMethods } from 'contexts/enabledPaymentMethods';
import { AccountProvider, useAccount } from 'contexts/account';
import { Account } from 'types/account';
import { UserPaymentMethod } from 'types/PaymentMethods';
import { ApiError } from 'utils/api';
import { Divider, Header, Message } from 'semantic-ui-react';

export default function ConfirmCardOrder() {
  const queryClient = useMemo(() => {
    return new QueryClient();
  }, []);

  return (
    <AppWrapper>
      <AccountProvider>
        <QueryClientProvider client={queryClient}>
          <Page />
        </QueryClientProvider>
      </AccountProvider>
    </AppWrapper>
  );
}

const Page = () => {
  const { t } = useTranslation();
  const [error, setError] = React.useState<string>();
  const [orderIsPlaced, setOrderIsPlaced] = useState(false);

  const billingPlanData = useChargeCardBillingPlans();

  const { productInstrumentation } = useProductInstrumentation();

  const { account } = useAccount();

  const { enabledPaymentMethods } = useEnabledPaymentMethods();

  const submitCardRequest = useSubmitCardRequest({
    onSuccess: async () => {
      setOrderIsPlaced(true);
    },
    onError: (error) => {
      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        stage: 'errored',
        step: 'plan-selection',
        version: '1.1.0',
      });

      setError(JSON.stringify(error));
    },
  });

  const confirmOrderFlow = async (
    account: Account,
    enabledPaymentMethods: UserPaymentMethod[]
  ) => {
    try {
      const paymentInstrumentSaved = await tryStoringPaymentInstrument(
        account,
        enabledPaymentMethods
      );

      if (!paymentInstrumentSaved) {
        setError('Payment instrument not saved. Please try again.');
        return;
      }

      // Get the request back from localStorage
      const orderChargeCardRequest = localStorage.getItem(
        'orderChargeCardRequest'
      );

      if (!orderChargeCardRequest) {
        setError('No order found. Please try again.');
        return;
      }
      const order = JSON.parse(orderChargeCardRequest);

      submitCardRequest.mutate(order);
      const dataLayer = (window as any).dataLayer;
      if (dataLayer) {
        dataLayer.push({
          event: 'card_requested',
          accountBillingCountry: order.billing?.countryCode?.toUpperCase(),
          billingPlanId: order.card.billingPlanId,
          billingPlanName: billingPlanData.data?.find(
            (c) => c.id === order.card.billingPlanId
          )?.name,
        });
      }

      // Remove the order from localStorage
      localStorage.removeItem('orderChargeCardRequest');
    } catch (e) {
      console.error('Error storing payment instrument:', e);
      setError(
        (e as ApiError).message || 'unkown error' // Fallback to a generic error message
      );
    }
  };

  useEffect(() => {
    if (!account || !enabledPaymentMethods) {
      return;
    }
    confirmOrderFlow(account, enabledPaymentMethods);
  }, [account, enabledPaymentMethods]);
  return (
    <>
      <Header as="h1">
        {t('confirmCardOrder.title', 'Confirm Card Order')}
      </Header>

      <Divider />

      {!error && !orderIsPlaced && (
        <Message info>
          {t('confirmCardOrder.description', 'We are processing your order')}
        </Message>
      )}

      {orderIsPlaced && (
        <Message success>
          {t('confirmCardOrder.success', 'Order placed successfully')}
        </Message>
      )}

      {error && error.length > 0 && !orderIsPlaced && (
        <Message error>
          {t(
            'confirmCardOrder.error',
            'There was an error processing your order. Please try again.'
          )}
          {error}
        </Message>
      )}
    </>
  );
};
